import { apiClient, ApiError } from './apiClient';
import { notificationService } from './notificationService';

/**
 * A bot message that answers a CAD check (leg 5). It holds only what the workflow cannot derive:
 * the record it is about and the facts of its own requests. Everything shown (reading, checking,
 * the findings, the apply offer) is rendered from the call page's live CAD state, so the chat
 * never holds a second copy of the record.
 */
export interface CadCheckRef {
  /** The record the reply is about; null while the upload runs, or when nothing was attached when asked. */
  attachmentId: string | null;
  attaching: boolean;
  /** This message asked for the check itself (the agency's automatic check is off) and the request failed. */
  requestFailed: boolean;
  /** "Not now", or an apply from this message: the action bar goes away, the findings stay. */
  dismissed: boolean;
}

export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  conversationId?: string;
  sources?: string[];
  /** The CAD file the user attached with this message, shown as a chip. Name and size only. */
  attachment?: { fileName: string; sizeBytes: number };
  /** Set on a bot message that answers a CAD check; its content is derived from the record when shown. */
  cadCheck?: CadCheckRef;
}

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatQueryRequest {
  query: string;
  conversationId?: string;
  userId?: string;
  conversationHistory?: ConversationMessage[];
  callContext?: {
    callId: string;
    transcription: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analysisResults?: any;
    /**
     * The call's CAD record to answer with. A reference only: the server loads the record for
     * the verified call and refuses one that is not on it; nothing from the record is sent.
     */
    cadAttachmentId?: string;
  };
  /**
   * Call IDs scoping the query to the result set the user is viewing
   * ("Current results" scope). The server re-validates every ID — caps the
   * count, reloads from the tenant DB, and drops calls outside the caller's
   * operator scope — so this is a hint, never an authorization.
   */
  scopeCallIds?: string[];
}

export interface ChatQueryResponse {
  response: string;
  conversationId: string;
  sources: string[];
  timestamp: string;
  /** Set only for scoped-results queries: calls actually analyzed after server re-validation. */
  scopeIncludedCount?: number;
  /** Set only for scoped-results queries: scope IDs the client sent. */
  scopeRequestedCount?: number;
  /**
   * Set only when the request referenced a CAD record: false means the record had nothing
   * extracted yet (a re-read in flight) and the answer came from the transcript alone.
   */
  cadRecordIncluded?: boolean;
}

export interface DocumentUploadRequest {
  fileName: string;
  content: string;
  description?: string;
  category?: string;
}

export interface DocumentUploadResponse {
  documentId: string;
  fileName: string;
  processingSuccess: boolean;
  errorMessage?: string;
}

export interface RagDocument {
  id: string;
  fileName: string;
  description: string;
  category: string;
  uploadedDate: string;
  uploadedBy: string;
  isActive: boolean;
}

class ChatbotService {
  async sendMessage(request: ChatQueryRequest): Promise<ChatQueryResponse> {
    try {
      const response = await apiClient.post<ChatQueryResponse>('/api/chatbot/query', request);
      return response.data;
    } catch (error) {
      notificationService.handleApiError(error as ApiError, 'Chatbot Query');
      throw error;
    }
  }

  async uploadDocument(request: DocumentUploadRequest): Promise<DocumentUploadResponse> {
    try {
      const response = await apiClient.post<DocumentUploadResponse>(
        '/api/chatbot/documents',
        request
      );

      if (response.data.processingSuccess) {
        notificationService.success(
          'Document Uploaded',
          `${request.fileName} has been uploaded and processed successfully.`
        );
      } else {
        notificationService.warning(
          'Document Upload Warning',
          response.data.errorMessage || 'Document uploaded but processing encountered issues.'
        );
      }

      return response.data;
    } catch (error) {
      notificationService.handleApiError(error as ApiError, 'Document Upload');
      throw error;
    }
  }

  async getDocuments(): Promise<RagDocument[]> {
    try {
      const response = await apiClient.get<RagDocument[]>('/api/chatbot/documents');
      return response.data;
    } catch (error) {
      notificationService.handleApiError(error as ApiError, 'Load Documents');
      throw error;
    }
  }

  async deleteDocument(documentId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/chatbot/documents/${documentId}`);
      notificationService.success(
        'Document Deleted',
        'The document has been removed successfully.'
      );
    } catch (error) {
      notificationService.handleApiError(error as ApiError, 'Delete Document');
      throw error;
    }
  }

  async updateDocument(documentId: string, document: RagDocument): Promise<void> {
    try {
      await apiClient.put(`/api/chatbot/documents/${documentId}`, document);
      notificationService.success(
        'Document Updated',
        'The document has been updated successfully.'
      );
    } catch (error) {
      notificationService.handleApiError(error as ApiError, 'Update Document');
      throw error;
    }
  }
}

export const chatbotService = new ChatbotService();
